import { Autocomplete, Fade } from "@mui/material";
import { useAppSelector } from "../../../store/hooks";
import { selectActiveRobotVersion } from "../../../store/ses/sesRobotSlice";
import { selectClusterServer } from "../../../store/serverSlice";
import useAccessRight from "../../../hooks/useAccessRight";
import useTranslate from "../../../hooks/useTranslate";
import { SES } from "../../../constants/accessRights";
import { ServiceTypeModelRobot } from "../../../types/cloudTypes";
import { RequestStatus } from "../../../types/statusTypes";
import Models from "../Models/Models";
import FormAddingModelConfig from "../../Forms/FormAddingModelConfig/FormAddingModelConfig";
import ProgressCircle from "../../../ui/ProgressCircle/ProgressCircle";
import { IModelsTabProps } from "./ModelsTab.props";
import styles from "./ModelsTab.module.scss";
import { CustomTextField } from "@/ui";
import { RenderOption } from "@/ui";

const ModelsTab = ({
	robotData,
	changeFlgHandler,
	inputVoice,
	setInputVoice,
	inputMainRecognitionModel,
	setInputMainRecognitionModel,
	inputBackgroundRecognitionModel,
	setInputBackgroundRecognitionModel,
}: IModelsTabProps): JSX.Element => {
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const { smc: clusterServerSmc, see: clusterServerSee, spr: clusterServerSpr, tts: clusterServerTts } = useAppSelector(selectClusterServer); // store - список моделей на сервере cluster

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	return (
		<Fade in={true} timeout={500}>
			<div className={styles.models}>
				<div className={styles.block}>
					{/* голос */}
					<Autocomplete
						fullWidth
						freeSolo
						options={clusterServerTts.modelList}
						value={inputVoice}
						onChange={(_, value) => {
							setInputVoice(value ? value : "");
							changeFlgHandler("voice"); // ставим флаг о несохраненных данных
						}}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						renderInput={(params) => (
							<CustomTextField
								{...params}
								hint={translate("hint_modelVoice")}
								required
								label={translate("input_voice")}
								onChange={(e) => {
									setInputVoice(e.target.value);
									changeFlgHandler("voice"); // ставим флаг о несохраненных данных
								}}
								error={!inputVoice}
								InputProps={{
									...params.InputProps, // важно прокинуть параметры
									endAdornment: (
										<div>
											{clusterServerTts.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
											{params.InputProps.endAdornment} {/* важно дописать параметры */}
										</div>
									),
								}}
							/>
						)}
						renderOption={(props, option) => {
							return <RenderOption {...props}>{option}</RenderOption>;
						}}
					/>
					{/* основная модель распознавания */}
					<Autocomplete
						freeSolo
						fullWidth
						options={clusterServerSpr.modelList}
						value={inputMainRecognitionModel}
						onChange={(_, value) => {
							setInputMainRecognitionModel(value ? value : "");
							changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
						}}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						renderInput={(params) => (
							<CustomTextField
								{...params}
								hint={translate("hint_basicRecognitionModel")}
								required
								label={translate("input_mainRecognitionModel")}
								onChange={(e) => {
									setInputMainRecognitionModel(e.target.value);
									changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
								}}
								error={!inputMainRecognitionModel}
								InputProps={{
									...params.InputProps, // важно прокинуть параметры
									endAdornment: (
										<div>
											{clusterServerSpr.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
											{params.InputProps.endAdornment} {/* важно дописать параметры */}
										</div>
									),
								}}
							/>
						)}
						renderOption={(props, option) => {
							return <RenderOption {...props}>{option}</RenderOption>;
						}}
					/>
					{/* фоновая модель распознавания */}
					<Autocomplete
						fullWidth
						freeSolo
						options={clusterServerSpr.modelList}
						value={inputBackgroundRecognitionModel}
						onChange={(_, value) => {
							setInputBackgroundRecognitionModel(value ? value : "");
							changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
						}}
						disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
						renderInput={(params) => (
							<CustomTextField
								{...params}
								hint={translate("hint_backgroundRecognitionModel")}
								required
								label={translate("input_backgroundRecognitionModel")}
								onChange={(e) => {
									setInputBackgroundRecognitionModel(e.target.value);
									changeFlgHandler("recognition"); // ставим флаг о несохраненных данных
								}}
								error={!inputBackgroundRecognitionModel}
								InputProps={{
									...params.InputProps, // важно прокинуть параметры
									endAdornment: (
										<div>
											{clusterServerSpr.status === RequestStatus.LOADING && <ProgressCircle isBtnDisabled />}
											{params.InputProps.endAdornment} {/* важно дописать параметры */}
										</div>
									),
								}}
							/>
						)}
						renderOption={(props, option) => {
							return <RenderOption {...props}>{option}</RenderOption>;
						}}
					/>
				</div>

				<div className={styles.modelSettings}>
					{Object.keys(robotData.models).map((serviceType) => {
						const serviceTypeModel = serviceType as ServiceTypeModelRobot;
						return Object.keys(robotData.models[serviceTypeModel]).map((model) => {
							return (
								<Models
									key={model}
									serviceType={serviceTypeModel}
									modelName={model}
									configData={robotData.models[serviceTypeModel][model]}
									modelsList={
										serviceTypeModel === "smc" ? clusterServerSmc.modelList : serviceTypeModel === "see" ? clusterServerSee.modelList : clusterServerTts.modelList
									}
									changeFlgHandler={changeFlgHandler}
								/>
							);
						});
					})}

					{isAccess(SES.ROBOT_EDIT) && activeRobotVersion === "draft" && (
						<FormAddingModelConfig
							changeFlgHandler={changeFlgHandler}
							modelsListSmc={clusterServerSmc.modelList}
							modelsListSee={clusterServerSee.modelList}
							modelsListTts={clusterServerTts.modelList}
						/>
					)}
				</div>
			</div>
		</Fade>
	);
};

export default ModelsTab;
